import { useDispatch, useSelector } from "react-redux";
import React, { useState, useMemo } from "react";
import { fetchPorAsins } from "../porAsinList/porAsinListSlice";
import { RootState } from "../app/rootReducer";
import { Button, Icon } from "@amzn/awsui-components-react";
import { trackButtonClick } from "../telemetry/telemetry";
import {
  addNotification,
  clearNotifications,
  setNotification,
} from "../notification/notificationConnector";
import { IN_PROGRESS_NOTIF_ITEMS } from "../notification/notification-config";
import {
  BuyBoxResult,
  fetchBuyBoxStatus,
  searchPorAsinsNoLimit,
} from "../app/api";

import {
  FullAsinWithAudits,
  RestrictedAsin,
} from "../../server/asin-central/model";
import { defaultSort } from "../porAsinList/TableCommonElements";
import { SortFacet } from "../../server/asin-central/search";

const MAX_COUNT_FETCH_BUY_BOX_STATUS = 1000;

export const FetchBuyBoxButton = () => {
  const dispatch = useDispatch();
  const [fetchInProgress, setFetchInProgress] = useState<boolean>(false);
  const { queryString } = useSelector(
    (state: RootState) => state.generateQuery
  );
  const { preferences, sort, descending } = useSelector(
    (state: RootState) => state.porAsinPageControl
  );
  const { count } = useSelector((state: RootState) => state.porAsinList);

  const enableFetchBuyBoxButton = useMemo(() => {
    return (
      (preferences.visibleContent ?? []).includes("buyBoxStatus") &&
      count < MAX_COUNT_FETCH_BUY_BOX_STATUS
    );
  }, [preferences.visibleContent, count]);

  //calls get
  async function fetchLatestBuyBoxStatus(
    asins: (FullAsinWithAudits | RestrictedAsin)[]
  ): Promise<BuyBoxResult> {
    const nonRestrictedAsins = asins.filter(
      (asin) =>
        (asin as FullAsinWithAudits) &&
        !asin.isRestricted &&
        Object.prototype.hasOwnProperty.call(asin, "asin") &&
        Object.prototype.hasOwnProperty.call(asin, "obfuscatedMarketplaceId")
    ) as FullAsinWithAudits[];

    return fetchBuyBoxStatus(nonRestrictedAsins);
  }

  const sleep = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const getSort = (): SortFacet[] =>
    sort === undefined
      ? defaultSort
      : [
          {
            attributeName: sort?.sortingField,
            ascending: !descending,
          },
        ];

  async function getLatestBuyBoxStatusData() {
    dispatch(clearNotifications());
    dispatch(
      addNotification(IN_PROGRESS_NOTIF_ITEMS("Fetching BuyBox Status"))
    );

    try {
      const asins = await searchPorAsinsNoLimit(queryString);
      const result = await fetchLatestBuyBoxStatus(asins);
      console.log(result);

      dispatch(
        setNotification({
          type: "info",
          dismissible: true,
          header:
            "Successfully refreshed BuyBox Status. Automatically refreshing data in 5 seconds...",
        })
      );
      await sleep(5000);
      dispatch(
        fetchPorAsins(0, preferences.pageSize, queryString, getSort(), true)
      );
    } catch (error) {
      dispatch(
        setNotification({
          type: "error",
          dismissible: true,
          header:
            "Failed to refresh BuyBox Status. This might be due to network issues or invalid data. If issue persists, use give feedback in the footer to rpeort an issue",
        })
      );
    } finally {
      setFetchInProgress(false);
    }
  }

  const disabledReason = useMemo(() => {
    if (fetchInProgress) {
      return "Fetch in progress...";
    }
    if (!enableFetchBuyBoxButton) {
      return `The refresh button will only be active once you enable the BuyBox Status column in preferences ⚙️. There is a maximum of ${MAX_COUNT_FETCH_BUY_BOX_STATUS} ASIN/MPs per ad-hoc refresh.`;
    }
    return `Click to refresh BuyBox Status for ${count} selected ASINs`;
  }, [fetchInProgress, enableFetchBuyBoxButton, count]);

  return (
    <div>
      <Button
        onClick={(event) => {
          trackButtonClick("asinCentralTable", "fetchBuyBox", {});
          setFetchInProgress(true);
          getLatestBuyBoxStatusData();
        }}
        disabled={fetchInProgress || !enableFetchBuyBoxButton}
        disabledReason={disabledReason}
      >
        <Icon name="refresh" size="normal" variant="normal" /> Refresh BuyBox
        Status
      </Button>
    </div>
  );
};
